import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BatchCombobox } from "@/components/batches/BatchCombobox";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { asArray, cn } from "@/lib/utils";
import { Loader2, UserPlus, ArrowLeft } from "lucide-react";
import {
  suggestBatches,
  parseAgeBand,
  calculateAgeYears,
  type SuggestBatchesBatchInput,
  type SuggestBatchesPlayerInput,
} from "@shared/batchSuggestion";

// players.ballType ("Tennis ball" | "Hard/Leather ball") uses a different
// vocabulary from batches.ballType ("tennis" | "hard") by design — see
// shared/schema.ts. Normalize here, at the one place both sides meet.
function normalizeBallType(formValue: string): string {
  if (formValue === "Tennis ball") return "tennis";
  if (formValue === "Hard/Leather ball") return "hard";
  return "";
}

interface ApiBatch {
  id: number;
  name: string;
  ballType: string | null;
  skillLevel: string | null;
  ageRange: string;
  days: string[] | null;
  timeSlot: string | null;
  sessionsPerWeek: number | null;
  maxCapacity: number | null;
  currentEnrollment: number;
  isActive: boolean;
}

function toSuggestionBatchInput(b: ApiBatch): SuggestBatchesBatchInput {
  return {
    id: b.id,
    name: b.name,
    ballType: (b.ballType ?? "").toLowerCase(),
    level: (b.skillLevel ?? "").toLowerCase(),
    ageBand: b.ageRange,
    days: b.days ?? [],
    timeSlot: b.timeSlot,
    sessionsPerWeek: b.sessionsPerWeek,
    capacity: b.maxCapacity ?? 0,
    currentCount: b.currentEnrollment,
  };
}

interface AddKidFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  playerType: string;
  ballType: string;
  skillLevel: string;
  preferredDays: string[];
  timeSlot: string;
  parentName: string;
  parentPhone: string;
  parentEmail: string;
  medicalNotes: string;
  batchId: string;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function calculateAge(dobString: string): number | null {
  if (!dobString) return null;
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export default function AddKid() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<AddKidFormData>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    playerType: "",
    ballType: "",
    skillLevel: "",
    preferredDays: [],
    timeSlot: "",
    parentName: "",
    parentPhone: "",
    parentEmail: "",
    medicalNotes: "",
    batchId: "",
  });
  const [showManualBatchPicker, setShowManualBatchPicker] = useState(false);

  const [errors, setErrors] = useState<Partial<Record<keyof AddKidFormData, string>>>({});

  const { data: batchesData } = useQuery({
    queryKey: ["/api/batches"],
    queryFn: async () => apiRequest("GET", "/api/batches"),
  });
  const batches: ApiBatch[] = asArray<ApiBatch>(batchesData?.data);

  // Suggestions only need the fields that are actually hard-required for
  // matching. timeSlot deliberately does NOT gate this — it's labeled
  // "(Optional)" in the form below, so a parent who skips it (the natural
  // thing to do, given the label) still needs to see suggestions. Gating
  // on it used to hide the entire suggestion section with zero explanation
  // whenever it was left blank — a real bug that silently dropped batch
  // assignment for real parents (confirmed live in production against two
  // real families before this fix). suggestBatches() already treats
  // preferredTimeSlot as an optional scoring bonus, never a hard filter,
  // so nothing downstream needs to change to support this. sessionsPerWeek
  // is no longer collected from the parent at all (removed — see
  // AddKidFormData); suggestBatches treats it as optional too.
  const suggestionReady = !!(
    formData.dateOfBirth &&
    formData.ballType &&
    formData.skillLevel &&
    formData.preferredDays.length > 0
  );

  const playerSuggestionInput: SuggestBatchesPlayerInput | null = suggestionReady
    ? {
        ballType: normalizeBallType(formData.ballType),
        level: formData.skillLevel.toLowerCase(),
        dob: formData.dateOfBirth,
        preferredDays: formData.preferredDays,
        preferredTimeSlot: formData.timeSlot,
      }
    : null;

  const suggestionResult = useMemo(() => {
    if (!playerSuggestionInput) return null;
    return suggestBatches(playerSuggestionInput, batches.map(toSuggestionBatchInput));
  }, [playerSuggestionInput, batches]);

  // Manual fallback list: same ballType/skillLevel/age eligibility as the
  // suggestion algorithm's hard filters, but every eligible batch (not just
  // top 3) — a parent browsing manually should see every real option.
  const manualEligibleBatches = useMemo(() => {
    if (!playerSuggestionInput) return [];
    const ageYears = calculateAgeYears(playerSuggestionInput.dob);
    return batches
      .map(toSuggestionBatchInput)
      .filter((b) => {
        if (b.ballType !== playerSuggestionInput.ballType) return false;
        if (b.level !== playerSuggestionInput.level) return false;
        if (b.currentCount >= b.capacity) return false;
        const band = parseAgeBand(b.ageBand);
        if (!band) return false;
        return ageYears >= band[0] && ageYears <= band[1];
      });
  }, [playerSuggestionInput, batches]);

  const createKidMutation = useMutation({
    mutationFn: async (data: AddKidFormData) => {
      const body = {
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth,
        // "Not sure yet" isn't a real position — send empty string so the
        // existing `playerType || null` handling in createPlayerHandler
        // stores it as NULL, same as leaving the field untouched.
        playerType: data.playerType === "Not sure yet" ? "" : data.playerType,
        medicalInformation: data.medicalNotes,
        ballType: data.ballType,
        skillLevel: data.skillLevel,
        preferredDays: data.preferredDays,
        timeSlot: data.timeSlot || undefined,
        parentName: data.parentName,
        parentPhone: data.parentPhone,
        parentEmail: data.parentEmail || undefined,
        batchId: data.batchId ? Number(data.batchId) : undefined,
      };

      const response = await fetch("/api/players", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create kid");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Kid added successfully",
      });

      // Invalidate kids query to refetch the list (must match the query key in KidsList)
      queryClient.invalidateQueries({ queryKey: ["/api/parent/kids"] });

      // Redirect to kids list
      navigate("/parent/kids");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add kid",
        variant: "destructive",
      });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AddKidFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      if (dob > today) {
        newErrors.dateOfBirth = "Date of birth cannot be in the future";
      }
    }

    if (!formData.ballType) {
      newErrors.ballType = "Ball type is required";
    }

    if (!formData.skillLevel) {
      newErrors.skillLevel = "Skill level is required";
    }

    if (formData.preferredDays.length === 0) {
      newErrors.preferredDays = "Select at least one preferred day";
    }

    // Real suggestions were shown but nothing was picked — block
    // submission rather than silently creating the kid with no batch.
    // suggestBatches() only ever returns a non-empty suggestions array
    // together with reason unset, so this can't misfire against the
    // genuine "no matching batch, staff assigns manually" case (reason
    // set, suggestions always empty there) — that path is real,
    // intentional, existing behavior, left untouched.
    if (suggestionResult && suggestionResult.suggestions.length > 0 && !formData.batchId) {
      newErrors.batchId = "Please select a batch below before adding your kid";
    }

    if (!formData.parentName.trim()) {
      newErrors.parentName = "Parent name is required";
    }

    if (!formData.parentPhone.trim()) {
      newErrors.parentPhone = "Parent phone is required";
    }

    if (!formData.parentEmail.trim()) {
      newErrors.parentEmail = "Parent email is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    createKidMutation.mutate(formData);
  };

  const handleChange = (field: keyof AddKidFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePreferredDaysChange = (days: string[]) => {
    setFormData((prev) => ({ ...prev, preferredDays: days }));
    if (errors.preferredDays) {
      setErrors((prev) => ({ ...prev, preferredDays: undefined }));
    }
  };

  const age = calculateAge(formData.dateOfBirth);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="max-w-2xl mx-auto p-4" data-testid="add-kid-page">
        <Button
          variant="ghost"
          onClick={() => navigate("/parent/kids")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Kids Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add New Kid
            </CardTitle>
            <CardDescription>
              Enter your child's information to add them to your account
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    data-testid="add-kid-name"
                    value={formData.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    placeholder="Enter first name"
                    className={errors.firstName ? "border-red-500" : ""}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    placeholder="Enter last name"
                    className={errors.lastName ? "border-red-500" : ""}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">
                  Date of Birth <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  data-testid="add-kid-age"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                  className={errors.dateOfBirth ? "border-red-500" : ""}
                />
                {age !== null && (
                  <p className="text-sm text-muted-foreground">Age: {age} years</p>
                )}
                {errors.dateOfBirth && (
                  <p className="text-sm text-red-500">{errors.dateOfBirth}</p>
                )}
              </div>

              {/* Playing Position */}
              <div className="space-y-2">
                <Label htmlFor="playerType" data-testid="add-kid-position">
                  Playing Position
                </Label>
                <Select
                  value={formData.playerType}
                  onValueChange={(value) => handleChange("playerType", value)}
                >
                  <SelectTrigger id="playerType">
                    <SelectValue placeholder="Select playing position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Batsman">Batsman</SelectItem>
                    <SelectItem value="Bowler">Bowler</SelectItem>
                    <SelectItem value="All-rounder">All-rounder</SelectItem>
                    <SelectItem value="Wicket-keeper">Wicket-keeper</SelectItem>
                    <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Ball Type */}
              <div className="space-y-2">
                <Label htmlFor="ballType">
                  Ball Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.ballType}
                  onValueChange={(value) => handleChange("ballType", value)}
                >
                  <SelectTrigger id="ballType" data-testid="add-kid-ball-type" className={errors.ballType ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select ball type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tennis ball">Tennis ball</SelectItem>
                    <SelectItem value="Hard/Leather ball">Hard/Leather ball</SelectItem>
                  </SelectContent>
                </Select>
                {errors.ballType && (
                  <p className="text-sm text-red-500">{errors.ballType}</p>
                )}
              </div>

              {/* Skill Level */}
              <div className="space-y-2">
                <Label htmlFor="skillLevel">
                  Skill Level <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.skillLevel}
                  onValueChange={(value) => handleChange("skillLevel", value)}
                >
                  <SelectTrigger id="skillLevel" data-testid="add-kid-skill-level" className={errors.skillLevel ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select skill level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
                {errors.skillLevel && (
                  <p className="text-sm text-red-500">{errors.skillLevel}</p>
                )}
              </div>

              {/* Preferred Days */}
              <div className="space-y-2">
                <Label>
                  Preferred Days <span className="text-red-500">*</span>
                </Label>
                <ToggleGroup
                  type="multiple"
                  value={formData.preferredDays}
                  onValueChange={handlePreferredDaysChange}
                  className="justify-start flex-wrap"
                  data-testid="add-kid-preferred-days"
                >
                  {DAYS.map((day) => (
                    <ToggleGroupItem
                      key={day}
                      value={day}
                      data-testid={`add-kid-day-${day}`}
                      className="border"
                    >
                      {day}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                {errors.preferredDays && (
                  <p className="text-sm text-red-500">{errors.preferredDays}</p>
                )}
              </div>

              {/* Time Slot */}
              <div className="space-y-2">
                <Label htmlFor="timeSlot">Preferred Time Slot (Optional)</Label>
                <Select
                  value={formData.timeSlot}
                  onValueChange={(value) => handleChange("timeSlot", value)}
                >
                  <SelectTrigger id="timeSlot" data-testid="add-kid-time-slot">
                    <SelectValue placeholder="Select time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Weekday 4:45–6:45 PM">Weekday 4:45–6:45 PM</SelectItem>
                    <SelectItem value="Weekday 6:45–8:45 PM">Weekday 6:45–8:45 PM</SelectItem>
                    <SelectItem value="Weekend morning">Weekend morning</SelectItem>
                    <SelectItem value="Weekend evening 5–7 PM">Weekend evening 5–7 PM</SelectItem>
                    <SelectItem value="Weekend evening 7–9 PM">Weekend evening 7–9 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Batch Suggestion */}
              {suggestionReady && suggestionResult && (
                <div className="space-y-2">
                  <Label>Suggested Batch</Label>
                  {suggestionResult.reason ? (
                    <p
                      className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3"
                      data-testid="add-kid-batch-no-match"
                    >
                      {suggestionResult.reason}
                    </p>
                  ) : (
                    <div className="space-y-2" data-testid="add-kid-batch-suggestions">
                      {suggestionResult.suggestions.map((s, i) => {
                        const freeSpots = s.capacity - s.currentCount;
                        const selected = formData.batchId === String(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => handleChange("batchId", String(s.id))}
                            data-testid={i === 0 ? "add-kid-batch-best-fit" : `add-kid-batch-alt-${i}`}
                            className={cn(
                              "w-full text-left rounded-lg border p-3 transition-colors",
                              i === 0 ? "border-2" : "text-sm",
                              selected
                                ? "border-emerald-500 bg-emerald-50"
                                : i === 0
                                ? "border-emerald-300"
                                : "border-gray-200 hover:border-gray-300",
                            )}
                          >
                            {i === 0 ? (
                              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                                Best fit
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500">or:</span>
                            )}
                            <p className={cn("text-gray-900", i === 0 ? "font-semibold" : "font-medium")}>
                              {s.name}
                            </p>
                            <p className="text-gray-600 text-sm">
                              {s.days.join(", ")} · {s.timeSlot ?? "—"} · {s.currentCount}/{s.capacity}
                              {freeSpots <= 2 && (
                                <span className="text-amber-600"> ({freeSpots} spot{freeSpots === 1 ? "" : "s"} left)</span>
                              )}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {errors.batchId && (
                    <p className="text-sm text-red-500" data-testid="add-kid-batch-required-error">
                      {errors.batchId}
                    </p>
                  )}

                  <div>
                    <button
                      type="button"
                      onClick={() => setShowManualBatchPicker((v) => !v)}
                      className="text-sm text-emerald-700 hover:underline"
                      data-testid="add-kid-batch-manual-toggle"
                    >
                      {showManualBatchPicker ? "Hide other batches" : "Choose a different batch"}
                    </button>
                    {showManualBatchPicker && (
                      <div className="mt-2">
                        <BatchCombobox
                          batches={manualEligibleBatches.map((b) => ({ id: b.id, name: b.name }))}
                          value={formData.batchId}
                          onValueChange={(value) => handleChange("batchId", value)}
                          placeholder="Select a batch"
                          testId="add-kid-batch-manual-select"
                          renderOption={(b) => {
                            const eligible = manualEligibleBatches.find((eb) => eb.id === b.id);
                            return (
                              <div className="flex flex-col py-0.5">
                                <span className="font-medium">{b.name}</span>
                                <span className="text-xs text-gray-500">
                                  {eligible?.days.join(", ") || "—"} · {eligible?.timeSlot ?? "—"} · {eligible?.sessionsPerWeek ?? "—"}x/week
                                </span>
                              </div>
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Parent Name */}
              <div className="space-y-2">
                <Label htmlFor="parentName">
                  Parent Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="parentName"
                  data-testid="add-kid-parent-name"
                  value={formData.parentName}
                  onChange={(e) => handleChange("parentName", e.target.value)}
                  placeholder="Enter parent name"
                  className={errors.parentName ? "border-red-500" : ""}
                />
                {errors.parentName && (
                  <p className="text-sm text-red-500">{errors.parentName}</p>
                )}
              </div>

              {/* Parent Phone + Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parentPhone">
                    Parent Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="parentPhone"
                    type="tel"
                    data-testid="add-kid-parent-phone"
                    value={formData.parentPhone}
                    onChange={(e) => handleChange("parentPhone", e.target.value)}
                    placeholder="Enter parent phone"
                    className={errors.parentPhone ? "border-red-500" : ""}
                  />
                  {errors.parentPhone && (
                    <p className="text-sm text-red-500">{errors.parentPhone}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parentEmail">
                    Parent Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="parentEmail"
                    type="email"
                    data-testid="add-kid-parent-email"
                    value={formData.parentEmail}
                    onChange={(e) => handleChange("parentEmail", e.target.value)}
                    placeholder="Enter parent email"
                    className={errors.parentEmail ? "border-red-500" : ""}
                  />
                  {errors.parentEmail && (
                    <p className="text-sm text-red-500">{errors.parentEmail}</p>
                  )}
                </div>
              </div>

              {/* Medical / Allergy Notes */}
              <div className="space-y-2">
                <Label htmlFor="medicalNotes">Medical / Allergy notes (Optional)</Label>
                <Textarea
                  id="medicalNotes"
                  data-testid="add-kid-medical-notes"
                  value={formData.medicalNotes}
                  onChange={(e) => handleChange("medicalNotes", e.target.value)}
                  placeholder="Any medical conditions or allergies we should know about..."
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/parent/kids")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="add-kid-submit"
                  disabled={createKidMutation.isPending}
                  className="flex-1"
                >
                  {createKidMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Kid
                    </>
                  )}
                </Button>
              </div>

              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/parent/kids")}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Kids Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
